import _ from 'underscore';

import {assertAndContinue} from 'Asserts';

export function toSeconds(timeMs: number): number {
  return Math.trunc(timeMs) / 1000;
}

/**
 * Regular expression to match a string that only contains lowercase alphanumeric characters, underscores, and hyphens.
 */
const DATADOG_TAG_REGEX_MATCHER = /^[a-z0-9_-]*$/;

/**
 * Converts a given string into a snake case-like string acceptable for datadog tags.
 *
 * Note, this is an approximation of snake case. It is converting all spaces
 * and hyphens to underscores and lowercasing all characters, but does not handle camelCase edge cases.
 * Technically hyphens are allowed in tag keys + values, but opting to replace them with underscores for consistency.
 *
 * See: https://docs.datadoghq.com/getting_started/tagging/#define-tags
 *
 * @param tagValue string to convert
 * @param options.removeInvalidCharacters if true, invalid characters will be removed from the string
 * @returns tagValue converted into a string with only alphanumeric lowercase characters and underscores
 */
export function toDatadogTagFormat(
  tagValue: string,
  options: {
    removeInvalidCharacters?: boolean;
  } = {}
): string {
  // Replace hyphens and spaces with underscores
  tagValue = tagValue.replace(/[-\s]/g, '_');

  // Ensure all characters are lowercase
  tagValue = tagValue.replace(/[A-Z]/g, (letter) => `${letter.toLowerCase()}`);

  // Remove invalid characters if requested
  if (options.removeInvalidCharacters) {
    tagValue = tagValue.replace(/[^a-z0-9_]/g, '');
  }

  assertAndContinue(
    DATADOG_TAG_REGEX_MATCHER.test(tagValue),
    'Input string contains invalid characters. Only alphanumeric characters and underscores are allowed.'
  );

  return tagValue;
}

/**
 * Ensure that all keys and values in the metadata object are in an acceptable format for datadog tags.
 * All keys and string tag values must be lowercase alphanumeric or underscore or hyphen characters.
 */
export function isInAcceptableDatadogTagFormat(metadata: Record<string, unknown>): boolean {
  return Object.keys(metadata).every((key) => {
    if (!DATADOG_TAG_REGEX_MATCHER.test(key)) {
      return false;
    }
    if (typeof metadata[key] === 'string') {
      return DATADOG_TAG_REGEX_MATCHER.test(metadata[key]);
    }
    return true;
  });
}

/**
 * Get a string representing a bucketed value to use in Datadog tag.
 *
 * Keep tag formatting in sync with tag_by_bucket in benchling/lib/observability/trace/tag_helpers.py
 *
 * Examples:
 * >>> tagByBucket(5, [5, 10])
 * "5-10"
 * >>> tagByBucket(10, [5, 10])
 * "10_or_greater"
 * @param value numeric value to place in a bucket
 * @param cuts list of cuts that define the buckets with inclusive lower bounds and exclusive upper bounds
 */
export function getBucketStringForTag(value: number, cuts: Array<number>): string {
  assertAndContinue(cuts.length > 0, 'Cuts must include at least one value.');
  const sortedCuts = _.sortBy(cuts, (a, b) => a - b);

  const max = sortedCuts.find((cut) => cut > value);
  const min = sortedCuts.findLast((cut) => cut <= value);
  if (min == null) {
    return `less_than_${max}`;
  } else if (max == null) {
    return `${min}_or_greater`;
  }
  return `${min}-${max}`;
}
